import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, LifeBuoy, Loader2, MonitorUp, RefreshCw, Rocket } from "lucide-react";
import { useAuth } from "@/components/auth/AuthContext";
import {
  OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT,
  useOperatingSystemPreviewCache,
  type OperatingSystemPreviewProviderLoadingDetail,
} from "@/components/operating-systems/OperatingSystemPreviewCacheContext";
import { useSupport } from "@/components/support/SupportContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useOperatingSystemProxyTicket } from "@/hooks/useOperatingSystemProxyTicket";
import { useRuntimeRecovery } from "@/hooks/useRuntimeRecovery";
import { supabase } from "@/integrations/supabase/client";
import {
  OPERATING_SYSTEM_APP_SAFE_SELECT,
  OPERATING_SYSTEM_JOB_SAFE_SELECT,
  OPERATING_SYSTEM_RUNTIME_CACHE_SAFE_SELECT,
  OPERATING_SYSTEM_RUNTIME_LIFECYCLE_SAFE_SELECT,
  OperatingSystemApp,
  OperatingSystemJob,
  OperatingSystemRuntimeCache,
  OperatingSystemRuntimeLifecycle,
  getOperatingSystemDetailPath,
  getOperatingSystemFeedbackLabel,
  getOperatingSystemPreviewDescriptor,
  getOperatingSystemRuntimeLifecycleFeedback,
  getOperatingSystemStatusLabel,
  getOperatingSystemStepLabel,
  getAppStatusDescriptor,
  getAppStatusFeedback,
  hasOperatingSystemRunnableRuntime,
  isActiveOsJobStatus,
  isActiveOsStatus,
} from "@/lib/operating-systems";

const RUN_REFRESH_MIN_INTERVAL_MS = 30_000;
const RUN_REFRESH_MAX_ATTEMPTS = 6;


function StatePanel({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          {icon}
        </div>
        <h1 className="mt-4 text-lg font-semibold text-foreground">{title}</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
        {children && <div className="mt-5 flex flex-wrap justify-center gap-2">{children}</div>}
      </div>
    </div>
  );
}

export default function OperatingSystemRun() {
  const { osId } = useParams<{ osId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading, isAnonymous } = useAuth();
  const { enabled: supportEnabled, openWidget } = useSupport();
  const {
    activatePreview,
    isPreviewLoaded,
    isPreviewFrameVisible,
    isPreviewUnavailable,
    keepPreviewAlive,
    prewarmPreview,
    reloadPreview,
  } = useOperatingSystemPreviewCache();

  const [app, setApp] = useState<OperatingSystemApp | null>(null);
  const [runtimeCache, setRuntimeCache] = useState<OperatingSystemRuntimeCache | null>(null);
  const [runtimeLifecycle, setRuntimeLifecycle] = useState<OperatingSystemRuntimeLifecycle | null>(null);
  const [job, setJob] = useState<OperatingSystemJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewTarget, setPreviewTarget] = useState<HTMLDivElement | null>(null);
  const runtimeRefreshLastAtRef = useRef(0);
  const runtimeRefreshAttemptsRef = useRef(0);
  const runtimeRefreshInFlightRef = useRef(false);

  const detailPath = osId ? getOperatingSystemDetailPath(osId) : "/applications";

  const loadOperatingSystem = useCallback(async () => {
    if (!osId || authLoading) return;
    if (!user?.id || isAnonymous) {
      setApp(null);
      setRuntimeCache(null);
      setRuntimeLifecycle(null);
      setJob(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: appRow, error: appError } = await supabase
        .from("operating_system_apps")
        .select(OPERATING_SYSTEM_APP_SAFE_SELECT)
        .eq("id", osId)
        .maybeSingle();
      if (appError) throw appError;
      if (!appRow) {
        setApp(null);
        setRuntimeCache(null);
        setRuntimeLifecycle(null);
        setJob(null);
        return;
      }

      const nextApp = appRow as unknown as OperatingSystemApp;
      setApp(nextApp);

      const { data: cacheRow, error: cacheError } = await supabase
        .from("operating_system_runtime_cache")
        .select(OPERATING_SYSTEM_RUNTIME_CACHE_SAFE_SELECT)
        .eq("operating_system_id", nextApp.id)
        .maybeSingle();
      if (cacheError) throw cacheError;
      setRuntimeCache((cacheRow || null) as unknown as OperatingSystemRuntimeCache | null);

      const { data: lifecycleRow, error: lifecycleError } = await supabase
        .from("operating_system_runtime_lifecycle")
        .select(OPERATING_SYSTEM_RUNTIME_LIFECYCLE_SAFE_SELECT)
        .eq("operating_system_id", nextApp.id)
        .maybeSingle();
      if (lifecycleError) throw lifecycleError;
      setRuntimeLifecycle((lifecycleRow || null) as unknown as OperatingSystemRuntimeLifecycle | null);

      if (nextApp.latest_job_id) {
        const { data: jobRow, error: jobError } = await supabase
          .from("operating_system_jobs")
          .select(OPERATING_SYSTEM_JOB_SAFE_SELECT)
          .eq("id", nextApp.latest_job_id)
          .maybeSingle();
        if (jobError) throw jobError;
        setJob((jobRow || null) as unknown as OperatingSystemJob | null);
      } else {
        setJob(null);
      }
    } catch (error) {
      console.error("Failed to load App run route:", error);
      toast({
        title: "Unable to load App",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAnonymous, osId, toast, user?.id]);

  useEffect(() => {
    void loadOperatingSystem();
  }, [loadOperatingSystem]);

  const active = !!app && (isActiveOsStatus(app.status) || isActiveOsJobStatus(job?.status));
  const runtimeAvailable = hasOperatingSystemRunnableRuntime(app, runtimeCache, runtimeLifecycle);
  const {
    ticket: proxyTicket,
    loading: proxyTicketLoading,
    error: proxyTicketError,
    refreshTicket,
  } = useOperatingSystemProxyTicket(app?.id, runtimeAvailable && !!user?.id && !isAnonymous, app?.version);

  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => {
      void loadOperatingSystem();
    }, 8000);
    return () => window.clearInterval(timer);
  }, [active, loadOperatingSystem]);

  const previewDescriptor = useMemo(
    () => getOperatingSystemPreviewDescriptor(app, runtimeCache, proxyTicket),
    [app, proxyTicket, runtimeCache],
  );
  const previewLoaded = isPreviewLoaded(previewDescriptor);
  const previewFrameVisible = isPreviewFrameVisible(previewDescriptor);
  const previewUnavailable = isPreviewUnavailable(previewDescriptor) && !previewFrameVisible;
  const readyForRuntime = runtimeAvailable;
  const progress = active ? job?.progress_percent ?? 5 : app?.status === "ready" ? 100 : 0;
  const authBlocked = app?.status === "auth_blocked" || app?.status === "update_auth_blocked";
  const statusDescriptor = app ? getAppStatusDescriptor({ app, job, lifecycle: runtimeLifecycle, runtimeCache }) : null;
  const storedFeedback = getOperatingSystemFeedbackLabel(app?.latest_job_feedback);
  const failureFeedback = getOperatingSystemFeedbackLabel(job?.error_message || app?.failure_reason);
  const stepFeedback = active ? getOperatingSystemStepLabel(job?.current_step) : null;
  const lifecycleFeedback = getOperatingSystemRuntimeLifecycleFeedback(runtimeLifecycle);
  const rawFeedback = app
    ? authBlocked
      ? storedFeedback || "Updates are paused until an admin reconnects the builder."
      : failureFeedback || storedFeedback || stepFeedback || lifecycleFeedback
    : null;
  const feedback = statusDescriptor ? getAppStatusFeedback(rawFeedback, statusDescriptor) : rawFeedback;
  const statusLabel = statusDescriptor?.label ?? null;
  const headerStatus = (() => {
    if (loading || authLoading) return "Opening App...";
    if (!user || isAnonymous) return "Sign in required";
    if (!app) return "App not found";
    return feedback || "Ready";
  })();
  const visibleHeaderStatus = statusLabel && headerStatus.trim().toLowerCase() === statusLabel.trim().toLowerCase()
    ? null
    : headerStatus;

  useEffect(() => {
    prewarmPreview(previewDescriptor);
  }, [prewarmPreview, previewDescriptor]);

  useEffect(() => {
    keepPreviewAlive(previewDescriptor);
  }, [keepPreviewAlive, previewDescriptor]);

  useLayoutEffect(() => {
    if (!previewDescriptor?.canEmbed || !previewTarget) return undefined;
    return activatePreview(previewDescriptor, previewTarget);
  }, [activatePreview, previewDescriptor, previewTarget]);

  useEffect(() => {
    if (!user?.id || !app?.id || isAnonymous) return;
    const openedAt = new Date().toISOString();
    supabase
      .from("user_operating_system_runtime_state")
      .upsert({
        user_id: user.id,
        last_opened_operating_system_id: app.id,
        last_opened_at: openedAt,
      }, { onConflict: "user_id" })
      .then(({ error }) => {
        if (error) console.warn("Failed to persist last opened App:", error);
      });
  }, [app?.id, isAnonymous, user?.id]);

  const refreshRuntimeUrl = useCallback(async (options: { force?: boolean; providerLoading?: boolean } = {}) => {
    if (!app?.id) return;
    if (runtimeRefreshInFlightRef.current) return;

    const now = Date.now();
    if (options.providerLoading && !runtimeRefreshLastAtRef.current && !options.force) {
      runtimeRefreshLastAtRef.current = now;
      return;
    }
    if (!options.force && now - runtimeRefreshLastAtRef.current < RUN_REFRESH_MIN_INTERVAL_MS) return;
    if (!options.force && runtimeRefreshAttemptsRef.current >= RUN_REFRESH_MAX_ATTEMPTS) {
      return;
    }

    runtimeRefreshInFlightRef.current = true;
    runtimeRefreshLastAtRef.current = now;
    runtimeRefreshAttemptsRef.current += 1;
    try {
      let status: string | undefined;
      try {
        const { data, error } = await supabase.functions.invoke("refresh-os-preview", {
          body: { operatingSystemId: app.id },
        });
        if (error) throw error;
        status = (data as { status?: string } | null)?.status;
      } catch (error) {
        console.warn("Failed to refresh App preview; renewing runtime ticket:", error);
      }
      await loadOperatingSystem();
      const nextTicket = await refreshTicket();
      if (nextTicket && previewDescriptor?.canEmbed && nextTicket.proxyUrl !== previewDescriptor.url) {
        reloadPreview({
          ...previewDescriptor,
          url: nextTicket.proxyUrl,
          embedUrl: nextTicket.proxyUrl,
          proxyExpiresAt: nextTicket.expiresAt,
        });
      }
      if (status === "ready" && previewLoaded) {
        runtimeRefreshAttemptsRef.current = 0;
      }
    } catch (error) {
      console.warn("Failed to refresh App runtime URL:", error);
    } finally {
      runtimeRefreshInFlightRef.current = false;
    }
  }, [app?.id, loadOperatingSystem, previewDescriptor, previewLoaded, refreshTicket, reloadPreview]);

  const runtimeRecoveryKey = runtimeAvailable && app?.id ? `${app.id}:${app.version ?? 0}` : null;
  const runtimeRecoveryKeyRef = useRef(runtimeRecoveryKey);
  runtimeRecoveryKeyRef.current = runtimeRecoveryKey;
  const runtimeRecovery = useRuntimeRecovery(
    runtimeRecoveryKey,
    previewLoaded,
    async () => {
      const requestedKey = runtimeRecoveryKeyRef.current;
      const nextTicket = await refreshTicket();
      if (requestedKey && requestedKey === runtimeRecoveryKeyRef.current && nextTicket && previewDescriptor?.canEmbed &&
        nextTicket.proxyUrl !== previewDescriptor.url) {
        reloadPreview({
          ...previewDescriptor,
          url: nextTicket.proxyUrl,
          embedUrl: nextTicket.proxyUrl,
          proxyExpiresAt: nextTicket.expiresAt,
        });
      }
    },
  );
  const loadSlow = runtimeRecovery.attempts >= 3;
  const loadTimedOut = runtimeRecovery.attempts >= 5 && !previewLoaded;

  useEffect(() => {
    if (!app?.id || !previewDescriptor?.cacheKey) return undefined;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<OperatingSystemPreviewProviderLoadingDetail>).detail;
      if (
        detail?.osId !== app.id ||
        detail.cacheKey !== previewDescriptor.cacheKey
      ) {
        return;
      }
      void refreshRuntimeUrl({ providerLoading: true });
    };
    window.addEventListener(OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT, handler);
    return () => window.removeEventListener(OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT, handler);
  }, [app?.id, previewDescriptor?.cacheKey, refreshRuntimeUrl]);

  const reloadRun = useCallback(() => {
    if (runtimeAvailable && !previewLoaded) {
      runtimeRecovery.retry();
      return;
    }
    if (previewDescriptor?.canEmbed) {
      reloadPreview(previewDescriptor, { force: true });
      void refreshRuntimeUrl({ force: true });
      return;
    }
    void loadOperatingSystem();
  }, [loadOperatingSystem, previewDescriptor, previewLoaded, refreshRuntimeUrl, reloadPreview, runtimeAvailable, runtimeRecovery]);

  const openSupport = useCallback(() => {
    if (supportEnabled) openWidget();
  }, [openWidget, supportEnabled]);

  const title = app?.name || "App";
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="group/os-shell fixed inset-x-0 top-0 z-40 h-20 pointer-events-none">
          <div className="absolute inset-x-0 top-0 h-4 pointer-events-auto" aria-hidden="true" />
          <div className="pointer-events-auto absolute inset-x-0 top-0 -translate-y-[calc(100%-0.375rem)] opacity-0 shadow-lg transition duration-200 ease-out motion-reduce:transition-none group-hover/os-shell:translate-y-0 group-hover/os-shell:opacity-100 group-focus-within/os-shell:translate-y-0 group-focus-within/os-shell:opacity-100">
            <header className="flex min-h-14 items-center justify-between gap-3 border-b border-border bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/85">
              <div className="flex min-w-0 items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate(detailPath)} aria-label="Back to Applyt">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="shrink-0 text-sm font-semibold text-primary">Applyt App</span>
                    <span className="truncate text-sm font-medium text-foreground">{title}</span>
                    {app && statusLabel && (
                      <Badge variant="outline" className={previewUnavailable ? "border-amber-300 bg-amber-50 text-amber-800" : statusDescriptor?.badgeClass}>
                        {previewUnavailable ? "Unavailable" : statusLabel}
                      </Badge>
                    )}
                  </div>
                  {statusDescriptor?.availabilityMessage && <p className="mt-0.5 text-xs text-muted-foreground">{statusDescriptor.availabilityMessage}</p>}
                  {visibleHeaderStatus && (
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {visibleHeaderStatus}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {!loading && !authLoading && (
                  <Button variant="outline" size="sm" onClick={reloadRun}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Reload
                  </Button>
                )}
              </div>
            </header>
            {active && (
              <div className="border-b border-border bg-muted/30 px-4 py-2">
                <Progress value={progress} className="h-1.5" />
              </div>
            )}
          </div>
        </div>

        {!authLoading && (!user || isAnonymous) ? (
          <StatePanel
            icon={<Rocket className="h-6 w-6" />}
            title="Sign in to open this App"
            description="This App is protected by your Applyt account."
          >
            <Button onClick={() => navigate("/auth")}>Sign in</Button>
            <Button variant="outline" onClick={() => navigate("/applications")}>Back to Applyt</Button>
          </StatePanel>
        ) : authLoading || (loading && !app) ? (
          <StatePanel
            icon={<Loader2 className="h-6 w-6 animate-spin" />}
            title="Opening App"
            description="Applyt is checking access and preparing the workspace shell."
          />
        ) : !app ? (
          <StatePanel
            icon={<AlertTriangle className="h-6 w-6" />}
            title="App not found"
            description="This App is missing, archived, or unavailable for this account."
          >
            <Button onClick={() => navigate("/applications")}>Back to Applyt</Button>
          </StatePanel>
        ) : readyForRuntime && previewDescriptor ? (
          <div className="relative min-h-0 flex-1 bg-white">
            {previewDescriptor.canEmbed && (
              <div ref={setPreviewTarget} className="absolute inset-0" aria-label={`${app.name} runtime`} />
            )}

            {!previewFrameVisible && !loadTimedOut && !previewUnavailable && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background px-4" role="status" aria-live="polite">
                <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 text-center shadow-sm">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                  <h1 className="mt-4 text-lg font-semibold text-foreground">
                    {loadSlow ? `Still opening ${app.name}` : `Opening ${app.name}`}
                  </h1>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {loadSlow
                      ? "Some generated Apps take a few minutes to wake up. Keep this tab open; Applyt will switch to the App as soon as it is ready."
                      : "The App can take a few seconds to wake up."}
                  </p>
                </div>
              </div>
            )}

            {(loadTimedOut || previewUnavailable) && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/95 px-4">
                <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 text-center shadow-sm">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  <h1 className="mt-4 text-lg font-semibold text-foreground">{previewUnavailable ? "Live workspace unavailable" : "App did not load"}</h1>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {previewUnavailable
                      ? "The saved workspace could not be opened. Try reconnecting. If this continues, contact support to check workspace access."
                      : "The workspace may be blocked or taking longer than expected. Stay in Applyt and try again shortly."}
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    <Button onClick={reloadRun}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Reload
                    </Button>
                    <Button variant="outline" onClick={() => navigate(detailPath)}>
                      Back to Applyt
                    </Button>
                    <Button variant="ghost" onClick={openSupport} disabled={!supportEnabled}>
                      <LifeBuoy className="mr-2 h-4 w-4" />
                      Support
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : readyForRuntime ? (
          <StatePanel
            icon={proxyTicketLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : <AlertTriangle className="h-6 w-6" />}
            title={proxyTicketLoading ? "Opening secure runtime" : "Runtime proxy unavailable"}
            description={proxyTicketError || "Applyt could not issue a secure runtime URL for this App yet."}
          >
            <Button onClick={reloadRun}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Reload
            </Button>
            <Button variant="outline" onClick={() => navigate(detailPath)}>Back to Applyt</Button>
            <Button variant="ghost" onClick={openSupport} disabled={!supportEnabled}>
              <LifeBuoy className="mr-2 h-4 w-4" />
              Support
            </Button>
          </StatePanel>
        ) : (
          <StatePanel
            icon={<MonitorUp className="h-6 w-6" />}
            title={app.status === "ready" ? "Workspace unavailable" : getOperatingSystemStatusLabel(app.status)}
            description={app.status === "ready"
              ? "This App has no workspace available to open. Contact support to check its runtime connection."
              : feedback || "Your App is being prepared."}
          >
            {active && <Progress value={progress} className="h-2 w-full" />}
            <Button variant="outline" onClick={() => navigate(detailPath)}>Back to Applyt</Button>
          </StatePanel>
        )}
      </div>
    </div>
  );
}
